import React, { useEffect, useState } from 'react';

import { store } from 'Models/Store';
import { CargoCharacteristicDto, getCargoCharacteristicTranslationId } from 'Modules/Cargo';
import alertToast from 'Util/ToastifyUtils';
import { useIntl } from 'react-intl';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { getCargoTypes, CargoTypeForVehicleDto } from 'Services/Api/_HumanWritten/UserVehicleService';
import { CustomerCargoProfileDto, saveCargoProfile } from 'Services/Api/_HumanWritten/CustomerCargoProfileService';

interface CargoProfileModalContentProps {
	userId: string;
	/** Provided when editing an existing profile. */
	profile?: CustomerCargoProfileDto;
	/** Cargo type ids that already have a profile (excluded from the select when creating). */
	existingCargoTypeIds: string[];
	onConfirm: () => void;
	onCancel: () => void;
	onSuccess: () => void;
}

/**
 * Profiles only customise NON-selectable characteristics — remove inherited ones, or add the special
 * (non-selectable) ones. Selectable characteristics stay a per-booking choice in the booking flow, so we
 * never surface them here. This keeps the profile's set disjoint from the booker's selectable picks.
 */
function nonSelectableCharacteristicIds(): Set<string> {
	return new Set(store.cargoCharacteristics.filter(c => !c.isSelectableForVehicle).map(c => c.id));
}

/** Inherited characteristics for a cargo type, restricted to the non-selectable (profile-managed) ones. */
function customisableInheritedIds(cargoTypeId: string): string[] {
	const nonSelectable = nonSelectableCharacteristicIds();
	return (store.cargoTypeCharacteristicsMap[cargoTypeId] ?? []).filter(id => nonSelectable.has(id));
}

function effectiveSelection(profile: CustomerCargoProfileDto): string[] {
	const nonSelectable = nonSelectableCharacteristicIds();
	const inherited = customisableInheritedIds(profile.cargoTypeId);
	const removed = new Set(profile.removedInheritedCharacteristicIds);
	// Effective selection = (inherited \ removed) ∪ added — restricted to profile-managed (non-selectable).
	return [
		...new Set([
			...inherited.filter(id => !removed.has(id)),
			...profile.addedCharacteristicIds.filter(id => nonSelectable.has(id)),
		]),
	];
}

function CargoProfileModalContent({
	userId,
	profile,
	existingCargoTypeIds,
	onConfirm,
	onCancel,
	onSuccess,
}: CargoProfileModalContentProps) {
	const intl = useIntl();
	const isCreate = !profile;
	const [cargoTypes, setCargoTypes] = useState<CargoTypeForVehicleDto[]>([]);
	const [cargoTypeId, setCargoTypeId] = useState<string>(profile?.cargoTypeId ?? '');
	const [selectedCharacteristicIds, setSelectedCharacteristicIds] = useState<string[]>(
		profile ? effectiveSelection(profile) : [],
	);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		getCargoTypes()
			.then(setCargoTypes)
			.catch(() => setCargoTypes([]));
	}, []);

	const cargoTypeOptions = cargoTypes
		.map(x => ({ value: x.id, label: `${x.cargoMake} - ${x.cargoModel}` }))
		.sort((a, b) => a.label.localeCompare(b.label));

	// When creating, only offer cargo types without an existing profile (one per type).
	const availableOptions = isCreate
		? cargoTypeOptions.filter(o => !existingCargoTypeIds.includes(o.value))
		: cargoTypeOptions;

	// Only non-selectable characteristics are managed in the profile; selectable ones stay a per-booking
	// choice in the booking flow.
	const customisableCharacteristics = store.cargoCharacteristics.filter(c => !c.isSelectableForVehicle);
	const inheritedIds = cargoTypeId ? customisableInheritedIds(cargoTypeId) : [];

	const onSelectCargoType = (newCargoTypeId: string) => {
		setCargoTypeId(newCargoTypeId);
		// Default the selection to the cargo type's (non-selectable) inherited characteristics.
		setSelectedCharacteristicIds(customisableInheritedIds(newCargoTypeId));
	};

	const toggleCharacteristic = (characteristicId: string) => {
		setSelectedCharacteristicIds(prev => {
			const selected = new Set(prev);
			if (selected.has(characteristicId)) {
				selected.delete(characteristicId);
			} else {
				selected.add(characteristicId);
			}
			return [...selected];
		});
	};

	const characteristicLabel = (characteristic: CargoCharacteristicDto) =>
		intl.formatMessage({
			id: getCargoCharacteristicTranslationId(characteristic),
			defaultMessage: characteristic.name,
		});

	const handleSave = async () => {
		if (!cargoTypeId) {
			alertToast('Select a cargo type', 'error', 'Error');
			return;
		}

		// Diff only against the profile-managed (non-selectable) inherited set, so hiding selectable
		// characteristics from the editor never marks them as removed.
		const inheritedSet = new Set(customisableInheritedIds(cargoTypeId));
		const selectedSet = new Set(selectedCharacteristicIds);
		const addedCharacteristicIds = [...selectedSet].filter(id => !inheritedSet.has(id));
		const removedInheritedCharacteristicIds = [...inheritedSet].filter(id => !selectedSet.has(id));

		setIsSaving(true);
		try {
			await saveCargoProfile(userId, {
				id: profile?.id,
				name: '',
				cargoTypeId,
				addedCharacteristicIds,
				removedInheritedCharacteristicIds,
			});
			alertToast(`Cargo profile ${isCreate ? 'added' : 'updated'}`, 'success', 'Saved');
			onSuccess();
			onConfirm();
		} catch (error) {
			alertToast(`Failed to ${isCreate ? 'add' : 'update'} cargo profile`, 'error', 'Error');
		} finally {
			setIsSaving(false);
		}
	};

	const title = isCreate ? 'Add cargo profile' : 'Edit cargo profile';

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>{title}</NewModal.Title>
				<Button
					className="new-modal--close"
					icon={{ icon: 'close', iconPos: 'icon-left' }}
					display={Display.Text}
					colors={Colors.Alternate}
					onClick={onCancel}
					labelVisible={false}
				/>
			</NewModal.Header>
			<NewModal.Body>
				<div className="cargo-profile-modal__form">
					<div data-test-id="cargo-profile-cargo-type-select">
						<SelectInput
							label="Cargo type"
							placeholder="Select cargo type"
							isDisabled={!isCreate || isSaving}
							isSearchable
							options={availableOptions}
							value={cargoTypeOptions.find(o => o.value === cargoTypeId) ?? null}
							onChange={(option: any) => onSelectCargoType(option?.value ?? '')}
						/>
					</div>

					{cargoTypeId && (
						<div className="cargo-profile-modal__characteristics">
							<p className="text-sm text-gray-700" style={{ margin: '12px 0 4px' }}>
								Cargo characteristics
							</p>
							<p className="text-xs text-gray-500" style={{ margin: '0 0 8px' }}>
								Selectable characteristics are chosen per booking and aren&apos;t managed here.
							</p>
							<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
								{customisableCharacteristics.map(characteristic => {
									const isInherited = inheritedIds.includes(characteristic.id);
									const suffix = isInherited ? ' (inherited)' : '';
									return (
										<CheckboxInput
											className="vehicle-characteristic-checkbox"
											key={characteristic.id}
											label={`${characteristicLabel(characteristic)}${suffix}`}
											checked={selectedCharacteristicIds.includes(characteristic.id)}
											disabled={isSaving}
											onChange={() => toggleCharacteristic(characteristic.id)}
										/>
									);
								})}
							</div>
						</div>
					)}
				</div>
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton
					onClick={onCancel}
					display={Display.Outline}
					colors={Colors.Grey}
					disabled={isSaving}
				>
					Cancel
				</NewModal.CancelButton>
				<NewModal.ConfirmButton
					onClick={handleSave}
					display={Display.Solid}
					colors={Colors.Secondary}
					disabled={isSaving}
				>
					{isSaving ? 'Saving...' : 'Save'}
				</NewModal.ConfirmButton>
			</NewModal.Footer>
		</>
	);
}

function openCargoProfileModal(content: React.ReactNode, modalTitle: string, onCancel: () => void): void {
	store.modal?.show(modalTitle, content, {
		className: 'cargo-profile-modal overflow',
		shouldCloseOnEsc: true,
		shouldCloseOnOverlayClick: false,
		onRequestClose: onCancel,
		newModal: true,
	});
}

export function openCargoProfileCreateModal(
	userId: string,
	existingCargoTypeIds: string[],
	onSuccess: () => void,
): Promise<boolean> {
	return new Promise<boolean>((resolve, reject) => {
		const onConfirm = () => {
			store.modal.hide();
			resolve(true);
		};
		const onCancel = () => {
			store.modal.hide();
			reject();
		};
		openCargoProfileModal(
			<CargoProfileModalContent
				userId={userId}
				existingCargoTypeIds={existingCargoTypeIds}
				onConfirm={onConfirm}
				onCancel={onCancel}
				onSuccess={onSuccess}
			/>,
			'Add Cargo Profile',
			onCancel,
		);
	});
}

export function openCargoProfileEditModal(
	userId: string,
	profile: CustomerCargoProfileDto,
	onSuccess: () => void,
): Promise<boolean> {
	return new Promise<boolean>((resolve, reject) => {
		const onConfirm = () => {
			store.modal.hide();
			resolve(true);
		};
		const onCancel = () => {
			store.modal.hide();
			reject();
		};
		openCargoProfileModal(
			<CargoProfileModalContent
				userId={userId}
				profile={profile}
				existingCargoTypeIds={[]}
				onConfirm={onConfirm}
				onCancel={onCancel}
				onSuccess={onSuccess}
			/>,
			'Edit Cargo Profile',
			onCancel,
		);
	});
}
